import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import { MdOutlineShowChart, MdOutlineStackedLineChart } from 'react-icons/md';
import {
    POPULARITY_METADATA_BY_KEY,
    POPULARITY_SORT_OPTIONS,
    SpateMetricMetadata,
} from 'utils/metrics';
import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadTimeSeriesAsCSV,
} from 'utils/formatCSV';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useCountryParam,
    useCurrentCountry,
    useEndDate,
    useTikTokWeeks,
} from 'utils/hooks';
import AddBySearch from 'components/Navigation/Search/AddBySearch';
import { BannerRow as BannerRowContent } from './BannerRow';
import DataTable from 'components/DataVis/DataTable/DataTable';
import Drawer from 'components/Drawer';
import ErrorBoundary from 'containers/ErrorBoundary';
import FilterSelect from 'components/Input/FilterSelect';
import { INSTAGRAM_COUNTRIES } from '@/config/constants';
import IconButton from 'components/Button/IconButton';
import { Message } from '../utils';
import NameCellWithTooltip from '../NameCellWithTooltip';
import OverviewDrawerV2 from 'components/OverviewDrawerV2';
import PopularityIndexRelatedEntitiesDrawer from 'components/DataVis/PopularityIndexRelatedEntitiesTable/PopularityIndexRelatedEntitiesDrawer';
import { SUPPORTED_TYPES_BY_DATASOURCE } from 'views/Watchlist/constants';
import Stack from 'components/Stack';
import WatchlistOverviewTimeSeries from 'components/DataVis/WatchlistOverviewTimeSeries';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import downloadImage from 'utils/downloadImage';
import { getOnChange } from 'components/SectionTableFilterChips';
import { getSeriesColors } from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart';
import { getWatchlistRenderHits } from '../Buttons';
import { healthStatFilterOptions } from 'utils/filters';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useCardsFilterSort } from 'components/CardsWrapper';
import { useHistory } from 'react-router-dom';
import { useOverviewWatchlistData } from './useOverviewWatchlistData';
import useTableColumns from 'utils/hooks/useTableColumns';

const INITIAL_NUM_LINES = 12;

const search_YoY = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.search_YoY,
    tableHeadColumnLabel: 'Search YoY',
    tableColumnWidthMin: 8,
});
const search_QoQ = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.search_QoQ,
    tableHeadColumnLabel: 'Search QoQ',
    tableColumnWidthMin: 8,
});
const search_MoM = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.search_MoM,
    tableHeadColumnLabel: 'Search MoM',
    tableColumnWidthMin: 8,
});
const tiktok_YoY = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.tiktok_YoY,
    tableHeadColumnLabel: 'TikTok YoY',
    tableColumnWidthMin: 8,
});
const tiktok_QoQ = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.tiktok_QoQ,
    tableHeadColumnLabel: 'TikTok QoQ',
    tableColumnWidthMin: 8,
});
const tiktok_MoM = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.tiktok_MoM,
    tableHeadColumnLabel: 'TikTok MoM',
    tableColumnWidthMin: 8,
});
const instagram_YoY = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.instagram_YoY,
    tableHeadColumnLabel: 'Instagram YoY',
    tableColumnWidthMin: 8,
});
const instagram_QoQ = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.instagram_QoQ,
    tableHeadColumnLabel: 'Instagram QoQ',
    tableColumnWidthMin: 8,
});
const instagram_MoM = new SpateMetricMetadata({
    ...POPULARITY_METADATA_BY_KEY.instagram_MoM,
    tableHeadColumnLabel: 'Instagram MoM',
    tableColumnWidthMin: 8,
});

const popIndexMetrics = [
    [POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month, true],
    [search_YoY, false],
    [search_QoQ, false],
    [search_MoM, false],
    [tiktok_YoY, false],
    [tiktok_QoQ, false],
    [tiktok_MoM, false],
    [instagram_YoY, false],
    [instagram_QoQ, false],
    [instagram_MoM, false],
    [POPULARITY_METADATA_BY_KEY.YoY, true],
    [POPULARITY_METADATA_BY_KEY.QoQ, true],
    [POPULARITY_METADATA_BY_KEY.MoM, true],
    [POPULARITY_METADATA_BY_KEY.increase_YoY, false],
    [POPULARITY_METADATA_BY_KEY.increase_QoQ, false],
    [POPULARITY_METADATA_BY_KEY.increase_MoM, false],
    [POPULARITY_METADATA_BY_KEY.predicted_YoY, false],
    [POPULARITY_METADATA_BY_KEY.convergence_prc, false],
    [POPULARITY_METADATA_BY_KEY.google_share_prc, true],
    [POPULARITY_METADATA_BY_KEY.tiktok_share_prc, true],
    [POPULARITY_METADATA_BY_KEY.instagram_share_prc, true],
];

const overviewHealthStatKeys = [
    'average monthly popularity',
    'popularity share',
    'popularity convergence',
    'popularity predicted growth',
];
const overviewHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => overviewHealthStatKeys.includes(label),
);

const TableTools = styled(Stack).attrs(
    ({ $direction = 'row', $space = '.5em' }) => ({
        $direction,
        $space,
    }),
)`
    display: flex;
    justify-content: flex-end;
`;

const OverviewWatchlist = memo(
    ({
        brandIds,
        categoryIds,
        relatedCategoryCategoryIds,
        relatedCategoryBrandIds,
        countryTextId,
        watchlistId,
        watchlistItems,
        onRemoveItem,
        onAddItem,
        watchlistName,
    }) => {
        const history = useHistory();
        const { mutateToWithCountryParam } = useCountryParam();
        const country = useCurrentCountry();
        const { week, year } = useTikTokWeeks();
        const date = useEndDate('pop-index');
        const timeSeriesRef = useRef();
        let filteredPopIndexMetrics = popIndexMetrics;
        if (country && !INSTAGRAM_COUNTRIES.includes(country.text_id)) {
            filteredPopIndexMetrics = popIndexMetrics.filter(
                ([{ key }]) =>
                    key !== 'instagram_YoY' &&
                    key !== 'instagram_QoQ' &&
                    key !== 'instagram_MoM',
            );
        }
        const popularitySortOptions = Object.values(
            POPULARITY_SORT_OPTIONS,
        ).filter(({ sortBy }) =>
            filteredPopIndexMetrics.some(([{ key }]) => key === sortBy),
        );
        const defaultInitialSortOption = popularitySortOptions.find(
            ({ sortBy }) =>
                sortBy ===
                POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month.key,
        );
        const [placeholderDataByType, setPlaceholderDataByType] = useState({
            category: [],
            brand: [],
            relatedCategoryCategory: [],
            relatedCategoryBrand: [],
        });
        const {
            overviewBrandsDataQuery,
            overviewCategoriesDataQuery,
            relatedCategoryCategoriesDataQuery,
            relatedCategoryBrandsDataQuery,
            allOverviewData,
            allRelatedCategoriesCategories,
            allRelatedCategoriesBrands,
        } = useOverviewWatchlistData({
            brandIds,
            categoryIds,
            relatedCategoryCategoryIds,
            relatedCategoryBrandIds,
            defaultInitialSortOption,
            placeholderDataByType,
        });

        const isPlaceholderDataLoaded =
            (categoryIds &&
                overviewCategoriesDataQuery.pages.filter(
                    (p) => p.isPlaceholderData,
                ).length) ||
            (brandIds &&
                overviewBrandsDataQuery.pages.filter((p) => p.isPlaceholderData)
                    .length);

        const isDataLoaded =
            !(
                overviewBrandsDataQuery.pages.filter((p) => p.isLoading)
                    .length ||
                overviewCategoriesDataQuery.pages.filter((p) => p.isLoading)
                    .length ||
                relatedCategoryCategoriesDataQuery?.isLoading ||
                relatedCategoryBrandsDataQuery?.isLoading
            ) || isPlaceholderDataLoaded;
        const unfilteredData = [];
        allOverviewData?.forEach((elem) => {
            if (elem.timeSeriesFullOverview) {
                const newElem = {
                    ...elem,
                    id: elem.category ? elem.category : elem.brand,
                    time_series: elem.timeSeriesFullOverview,
                    convergence_prc:
                        elem.healthStats &&
                        elem.healthStats['popularity convergence'] &&
                        elem.healthStats['popularity convergence'].preciseScore
                            ? elem.healthStats['popularity convergence']
                                  .preciseScore / 10
                            : undefined,
                };
                unfilteredData.push(newElem);
            } else if (elem.type === 'related-category') {
                const newElem = {
                    ...elem,
                    time_series: null,
                    convergence_prc:
                        elem.healthStats &&
                        elem.healthStats['popularity convergence'] &&
                        elem.healthStats['popularity convergence'].preciseScore
                            ? elem.healthStats['popularity convergence']
                                  .preciseScore / 10
                            : undefined,
                };
                unfilteredData.push(newElem);
            }
        });
        let handleQueryCacheOnRemoveItem = onRemoveItem;
        if (onRemoveItem && (isDataLoaded || isPlaceholderDataLoaded)) {
            handleQueryCacheOnRemoveItem = (node) => {
                setPlaceholderDataByType({
                    category: (overviewCategoriesDataQuery?.data || []).filter(
                        (d) => d.id !== node.id,
                    ),
                    brand: (overviewBrandsDataQuery?.data || []).filter(
                        (d) => d.id !== node.id,
                    ),
                    relatedCategoryCategory: (
                        allRelatedCategoriesCategories || []
                    ).filter((d) => d.id !== node.id),
                    relatedCategoryBrand: (
                        allRelatedCategoriesBrands || []
                    ).filter((d) => d.id !== node.id),
                });
                onRemoveItem(node);
            };
        } else if (onRemoveItem) {
            handleQueryCacheOnRemoveItem = () => {};
        }
        const handleQueryCacheOnAddItem = (node) => {
            const supportsHashtags =
                SUPPORTED_TYPES_BY_DATASOURCE.overview?.has('hashtag');
            if (node.type === 'hashtag' && !supportsHashtags) {
                toast.success(
                    `${node.name} added. Switching to TikTok tab to view it.`,
                    { position: 'top-right', duration: 4000 },
                );
                history.push(
                    mutateToWithCountryParam(
                        `/watchlist/${watchlistId}/tiktok`,
                    ),
                );
            }
            const skeletonNode = {
                id: node.id,
                fetching: true,
                fetchingData: true,
                [node.type === 'category' ? 'category' : 'brand']: node.id,
                timeSeriesFullOverview: [],
            };
            let placeholderTypeKey = '';
            if (node.type === 'category') {
                placeholderTypeKey = 'category';
            } else if (node.type === 'brand') {
                placeholderTypeKey = 'brand';
            } else if (node.type === 'related-category' && node.categoryId) {
                placeholderTypeKey = 'relatedCategoryCategory';
            } else if (node.type === 'related-category' && node.brandId) {
                placeholderTypeKey = 'relatedCategoryBrand';
            }
            setPlaceholderDataByType((prev) => ({
                ...prev,
                [placeholderTypeKey]: prev[placeholderTypeKey].concat([
                    skeletonNode,
                ]),
            }));
            onAddItem(node);
        };
        const renderHits = useMemo(
            () =>
                getWatchlistRenderHits({
                    watchlistItems,
                    onAddItem: onAddItem ? handleQueryCacheOnAddItem : null,
                    onRemoveItem: onRemoveItem
                        ? (elem) => {
                              const node = {
                                  [elem.type === 'category'
                                      ? 'category'
                                      : 'brand']: elem.id,
                                  id: elem.id,
                              };
                              handleQueryCacheOnRemoveItem(node);
                          }
                        : null,
                    dataSource: 'overview',
                }),
            [watchlistItems, onAddItem, onRemoveItem, allOverviewData],
        );
        const {
            filters,
            filtersKey,
            maxSortMetric,
            onSearchInputChange,
            setFilter,
            setFilters,
            resetFilters,
            setSortOption: baseSetSortOption,
            initialSortOption,
            sortOption,
            sortOptionKey,
            sortedData,
        } = useCardsFilterSort({
            rootCategoryId: watchlistId,
            data: unfilteredData,
            sortOptions: popularitySortOptions,
            initialSortOption: defaultInitialSortOption,
            filterMissingData: false,
        });
        const TABLE_NAME = 'watchlist-overview';
        const { visibleOrderedMetrics, columnsMenu, setSortOption } =
            useTableColumns({
                tableName: TABLE_NAME,
                metrics: filteredPopIndexMetrics,
                setSortOption: baseSetSortOption,
            });
        const visibleColumnsConfig = createDefaultColumns(
            ['name', ...visibleOrderedMetrics],
            { showHealthStatLabels: true },
        );

        visibleColumnsConfig[0].formatCell = ({ row, value }) => (
            <NameCellWithTooltip row={row} value={value} />
        );

        const setWatchlistSortOption = useCallback(
            (sortOption) => {
                setSortOption(
                    popularitySortOptions.find(
                        ({ sortBy, ascending }) =>
                            sortBy === sortOption.sortBy &&
                            ascending === sortOption.ascending,
                    ),
                );
            },
            [setSortOption],
        );

        const timeSeriesData = sortedData
            ? sortedData.filter(
                  ({ timeSeries }) => timeSeries && timeSeries.length,
              )
            : null;

        const noDataMessage = !sortedData
            ? ''
            : sortedData.length && !timeSeriesData.length
              ? `Not enough data to render time series chart.`
              : `No trends available.${Object.keys(filters).length ? ' Try adjusting the filters.' : ''}`;
        const [visibleIds, setVisibleIds] = useState(null);
        const [colorsById, setColorsById] = useState(null);
        const [removedColor, setRemovedColor] = useState(null);
        const [viewSumTimeSeries, setViewSumTimeSeries] = useState(false);

        useEffect(() => {
            setVisibleIds(null);
            setColorsById(null);
            setRemovedColor(null);
        }, [filters]);

        let data;
        let visibleTimeSeriesData;
        let timeSeriesDataIds;
        if (sortedData) {
            data = [];
            visibleTimeSeriesData = [];
            timeSeriesDataIds = [];
            for (let i = 0; i < sortedData.length; i++) {
                const row = sortedData[i];
                const hasTimeSeriesData =
                    row.timeSeries && row.timeSeries.length;
                const isInitialId =
                    !visibleIds &&
                    visibleTimeSeriesData.length < INITIAL_NUM_LINES &&
                    hasTimeSeriesData;
                const isVisibleId = visibleIds && visibleIds.includes(row.id);
                const checked = !!(isInitialId || isVisibleId);
                const color =
                    hasTimeSeriesData && checked
                        ? (colorsById && colorsById[row.id]) ||
                          getSeriesColors(visibleTimeSeriesData.length)
                        : undefined;
                const formattedRow = { ...row, checked, color };
                data.push(formattedRow);
                if (checked && hasTimeSeriesData)
                    visibleTimeSeriesData.push(formattedRow);
                if (hasTimeSeriesData) timeSeriesDataIds.push(row.id);
            }
        }

        const onToggleCheck = ({ node, checked }) => {
            const nextColorsById = colorsById
                ? { ...colorsById }
                : data.reduce((acc, { id, color }, idx) => {
                      if (color) acc[id] = color;
                      return acc;
                  }, {});

            if (!node) {
                const currentVisible = (
                    !visibleIds
                        ? data.filter(({ checked }) => checked)
                        : visibleIds
                ).length;
                setVisibleIds(
                    !checked && currentVisible < timeSeriesDataIds.length
                        ? timeSeriesDataIds
                        : !checked
                          ? []
                          : null,
                );
                setColorsById(null);
            } else if (checked && visibleIds && !visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.concat([node.id]));
                nextColorsById[node.id] =
                    removedColor || getSeriesColors(visibleIds.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else if (!checked && visibleIds && visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.filter((id) => id !== node.id));
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            } else if (checked) {
                setVisibleIds(
                    visibleTimeSeriesData.map(({ id }) => id).concat([node.id]),
                );
                nextColorsById[node.id] =
                    removedColor ||
                    getSeriesColors(visibleTimeSeriesData.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else {
                setVisibleIds(
                    visibleTimeSeriesData
                        .filter(({ id }) => id !== node.id)
                        .map(({ id }) => id),
                );
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            }
        };
        const unfilteredCount = unfilteredData?.filter(
            ({ time_series }) => time_series && time_series.length,
        ).length;
        const visibleCount = visibleTimeSeriesData
            ? visibleTimeSeriesData.length
            : 0;
        const mixpanelMeta = {
            contentKey: 'OverviewWatchlist',
            country: country ? country.name : null,
        };
        const renderDrawerContent = (selectedId) => {
            const dataPoint =
                selectedId &&
                sortedData &&
                sortedData.find(({ id }) => id === selectedId);
            if (!dataPoint) return <Message>Item not found.</Message>;
            if (dataPoint.type === 'related-category') {
                return (
                    <ErrorBoundary>
                        <PopularityIndexRelatedEntitiesDrawer
                            selectedId={dataPoint.relatedCategoryId}
                            selectedRow={dataPoint}
                            paginateBy={'categories'}
                            countryId={country ? country.id : null}
                            parentEntity={{
                                type: dataPoint.categoryId
                                    ? 'category'
                                    : 'brand',
                                name: dataPoint.parentTrendName,
                            }}
                            parentEntityId={
                                dataPoint.categoryId || dataPoint.brandId
                            }
                            week={week}
                            year={year}
                            showAddToWatchlist={false}
                        />
                    </ErrorBoundary>
                );
            }
            return (
                <ErrorBoundary>
                    <OverviewDrawerV2
                        paginateItem={dataPoint.paginateItem}
                        data={dataPoint.data}
                        parentCategory={{
                            hierarchies: dataPoint.hierarchies,
                            id: dataPoint.category,
                        }}
                        timeSeriesGoogle={dataPoint.timeSeriesGoogle}
                        timeSeriesTikTok={dataPoint.timeSeriesTikTok}
                        timeSeriesInstagram={dataPoint.timeSeriesInstagram}
                        timeSeriesCombined={dataPoint.timeSeriesCombined}
                        dataPoint={dataPoint}
                    />
                </ErrorBoundary>
            );
        };

        const rowHeight = sortedData?.find((s) => s.type === 'related-category')
            ? 60
            : 50;

        return (
            <>
                <div
                    css={`
                        padding: 1.25rem;
                        padding-top: 0;
                        margin: 0 -1.25rem 1.25rem;
                        border-bottom: 1px solid ${({ theme }) => theme.gray_5};
                        display: flex;
                        justify-content: space-between;
                    `}
                >
                    <IconButton
                        size="sm"
                        IconLeft={
                            viewSumTimeSeries
                                ? MdOutlineStackedLineChart
                                : MdOutlineShowChart
                        }
                        onClick={(e) =>
                            setViewSumTimeSeries(!viewSumTimeSeries)
                        }
                        mixpanelContext={
                            viewSumTimeSeries
                                ? 'View Separated Time Series'
                                : 'View Combined Time Series'
                        }
                        isDisabled={!visibleCount}
                    >
                        {viewSumTimeSeries ? 'View Separated' : 'View Combined'}
                    </IconButton>
                    <DownloadCSVMenuButton
                        isDisabled={!isDataLoaded || !date}
                        isLoading={!isDataLoaded || !date}
                        options={[
                            {
                                mixpanelMeta: { label: 'card data csv' },
                                children: 'Table Data CSV',
                                onClick: () =>
                                    downloadItemsAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'pop-index',
                                            type: 'trend',
                                            kind: 'data',
                                            date,
                                        }),
                                        sortedData,
                                        [
                                            {
                                                key: 'name',
                                                accessor: ({
                                                    exportName,
                                                    name,
                                                }) => exportName || name || '',
                                            },
                                            ...visibleOrderedMetrics.map(
                                                ({
                                                    key,
                                                    tableHeadColumnLabel: label,
                                                }) => ({ key, label }),
                                            ),
                                            {
                                                key: 'cluster',
                                                label: 'Cluster',
                                                accessor: ({ cluster }) =>
                                                    cluster
                                                        ? cluster.replace(
                                                              'tiktok ',
                                                              '',
                                                          )
                                                        : cluster,
                                            },
                                            { key: 'healthStats' },
                                        ],
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'card data csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series csv' },
                                children: 'Time Series CSV',
                                onClick: () =>
                                    downloadTimeSeriesAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'pop-index',
                                            type: 'trend',
                                            kind: 'time-series',
                                            date,
                                        }),
                                        sortedData,
                                        {
                                            tsColumns: [
                                                { key: 'date' },
                                                {
                                                    key: 'real_volume',
                                                    label: 'volume',
                                                    precision: 1,
                                                },
                                                { key: 'prediction' },
                                            ],
                                        },
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'time series csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series image' },
                                children: 'Time Series Image',
                                onClick: () =>
                                    downloadImage(timeSeriesRef.current, {
                                        title: `${watchlistName} (${country ? country.code || country.name : null})`,
                                        filename: buildExportFilename({
                                            date,
                                            country,
                                            source: 'pop-index',
                                            entity: watchlistName,
                                            type: 'trend',
                                            kind: 'time-series',
                                        }),
                                    }),
                                downloadType: downloadTypes.IMAGE,
                                key: 'time series image',
                            },
                        ]}
                        mixpanelMeta={mixpanelMeta}
                    />
                </div>
                <WatchlistOverviewTimeSeries
                    ref={timeSeriesRef}
                    noDataMessage={noDataMessage}
                    data={visibleTimeSeriesData}
                    isLoading={!isDataLoaded}
                    viewSumTimeSeries={viewSumTimeSeries}
                />
                <div
                    css={`
                        padding: ${({ theme }) => theme.space_8}px 0;
                        font-weight: bold;
                        font-size: 0.75em;
                    `}
                >
                    {`Showing ${!isDataLoaded ? '... of ...' : `${visibleCount} of ${unfilteredCount}`} items. Use the list to add and remove items.`}
                </div>
                <Stack $space={(theme) => `${theme.space_10}px;`}>
                    <TableTools
                        css={`
                            display: flex;
                            justify-content: space-between;
                        `}
                    >
                        <FilterSelect
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            resetFilters={resetFilters}
                            sortOption={sortOption}
                            setSortOption={setWatchlistSortOption}
                            initialSortOption={initialSortOption}
                            mixpanelMeta={mixpanelMeta}
                            menus={['life_cycle', 'cluster', 'healthStats']}
                            getOnChange={getOnChange}
                            healthStatFilterOptions={
                                overviewHealthStatFilterOptions
                            }
                            showPresetsMenu={false}
                            showAllHealthStats={true}
                            menuAlign="left"
                        />
                        <div
                            css={`
                                display: flex;
                                gap: 12px;
                            `}
                        >
                            {columnsMenu}
                            {renderHits ? (
                                <AddBySearch
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                    panelAlign="right"
                                    dataSource="overview"
                                />
                            ) : null}
                        </div>
                    </TableTools>
                    <DataTable
                        dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                        maxSortMetric={maxSortMetric}
                        onSearchInputChange={onSearchInputChange}
                        sortOption={sortOption}
                        setSortOption={setWatchlistSortOption}
                        initialSortOption={initialSortOption}
                        mixpanelMeta={mixpanelMeta}
                        noDataMessage={noDataMessage}
                        data={isDataLoaded || data?.length ? data : undefined}
                        name={watchlistName || TABLE_NAME}
                        onRemoveItem={handleQueryCacheOnRemoveItem}
                        onToggleCheck={onToggleCheck}
                        columns={visibleColumnsConfig}
                        renderDrawerContent={renderDrawerContent}
                        DrawerComponent={Drawer}
                        rowHeight={rowHeight}
                        BannerRowContent={
                            <BannerRowContent
                                watchlistId={watchlistId}
                                mixpanelMeta={mixpanelMeta}
                            />
                        }
                    />
                </Stack>
            </>
        );
    },
    isEqual,
);

export default OverviewWatchlist;
